import * as React from "react";

// material-ui
import {
  Avatar,
  AvatarGroup,
  Button,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  styled,
  useTheme,
} from "@mui/material";

//type
import { KeyedObject } from "types";

// project imports
import MainCard from "@stories/ui-component/cards/MainCard";
import { ProjectLineItem } from "@interfaces/project";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { gridSpacing } from "constant";
import EditIcon from "@mui/icons-material/Edit";
import Avatar2 from "assets/images/users/avatar-2.png";
import Avatar3 from "assets/images/users/avatar-3.png";
import Avatar4 from "assets/images/users/avatar-4.png";
import Avatar5 from "assets/images/users/avatar-5.png";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { NoRows } from "@stories/molecules/ListTable/NoRows";
import { DataLoadingRow } from "@stories/molecules/ListTable/DataLoadingRow";
import { Country } from "country-state-city";
import { BorderLinearProgress } from "@stories/atoms/ProgressBar/ProgressBar";

// style constant
const ProjectCard = (
  project: ProjectLineItem,
  index: number,
  handleViewProject: (project: ProjectLineItem) => void,
  handleEditProject: (project: ProjectLineItem) => void
) => {
  const projectCountry = Country.getCountryByCode(project.projectCountry);
  return (
    <TableRow key={index}>
      <TableCell>
        <Grid container spacing={2}>
          <Grid item>
            <Avatar
              alt="User 1"
              src={project.organisationLogo}
              sx={{ width: 60, height: 60 }}
            />
          </Grid>
          <Grid item sm zeroMinWidth>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography align="left" variant="subtitle1">
                  {project.projectName}{" "}
                  <CheckCircleIcon
                    sx={{
                      color: "success.dark",
                      width: 14,
                      height: 14,
                    }}
                  />
                </Typography>
                <Typography
                  align="left"
                  variant="subtitle2"
                  sx={{ whiteSpace: "break-spaces" }}
                >
                  {project.projectCity}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  align="left"
                  variant="body2"
                  sx={{ whiteSpace: "break-spaces" }}
                >
                  {project.organisationName || ""}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
      <TableCell sx={{ width: "25%" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="caption">Contact Name</Typography>
            <Typography variant="h6">{project.representativeName}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption">Email</Typography>
            <Typography variant="h6">{project.representativeEmail}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption">Address</Typography>
            <Typography variant="h6">{`${project.siteAddressLine1} ${project.sitePostCode}`}</Typography>
          </Grid>
        </Grid>
      </TableCell>
      <TableCell>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="caption">Collaborators</Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              <AvatarGroup
                max={4}
                sx={{
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    fontSize: "1rem",
                  },
                }}
              >
                <Avatar alt="User 1" src={project.organisationLogo} />
                <Avatar alt="User 2" src={Avatar2} />
                <Avatar alt="User 3" src={Avatar3} />
                <Avatar alt="User 4" src={Avatar4} />
                <Avatar alt="User 5" src={Avatar5} />
              </AvatarGroup>
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
      <TableCell>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={gridSpacing}>
              <Grid item>
                <Typography variant="caption">Progress</Typography>
              </Grid>
              <Grid item sm zeroMinWidth>
                <BorderLinearProgress
                  variant="determinate"
                  value={project.estimatedProgress}
                  color="primary"
                  sx={{ minWidth: 90 }}
                  aria-label="user-progress"
                />
              </Grid>
              <Grid item>
                <Typography variant="h6" component="div">
                  {`${project.estimatedProgress.toPrecision(1)}%`}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                fullWidth
                size="small"
                sx={{ minWidth: 120 }}
                startIcon={<RemoveRedEyeIcon />}
                onClick={() => handleViewProject(project)}
              >
                View
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                size="small"
                sx={{ minWidth: 120 }}
                startIcon={<EditIcon />}
                onClick={() => handleEditProject(project)}
              >
                Edit
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

interface ProjectTableProps {
  loading: boolean;
  projects: ProjectLineItem[];
  handleViewProject: (project: ProjectLineItem) => void;
  handleEditProject: (project: ProjectLineItem) => void;
  handleArchiveProject: (project: ProjectLineItem) => void;
}

export const ProjectTable = ({
  loading,
  projects,
  handleViewProject,
  handleEditProject,
  handleArchiveProject,
}: ProjectTableProps) => {
  const theme = useTheme();

  return (
    <MainCard content={false} title="Projects">
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{
            "& td": {
              whiteSpace: "nowrap",
            },
            "& td:first-of-type": {
              pl: 0,
            },
            "& td:last-of-type": {
              pr: 0,
              minWidth: 260,
            },
            "& tbody tr:last-of-type  td": {
              borderBottom: "none",
            },
            [theme.breakpoints.down("xl")]: {
              "& tr:not(:last-of-type)": {
                borderBottom: "1px solid",
                borderBottomColor:
                  theme.palette.mode === "dark"
                    ? "rgb(132, 146, 196, .2)"
                    : "rgba(224, 224, 224, 1)",
              },
              "& td": {
                display: "inline-block",
                borderBottom: "none",
                pl: 0,
              },
              "& td:first-of-type": {
                display: "block",
              },
            },
          }}
        >
          <TableBody>
            {!loading
              ? projects.map((row: KeyedObject, index: number) => {
                  return ProjectCard(
                    row as ProjectLineItem,
                    index,
                    handleViewProject,
                    handleEditProject
                  );
                })
              : null}
            {projects.length === 0 && !loading ? (
              <NoRows colSpan={5} title={"Projects"} />
            ) : null}
            {loading && <DataLoadingRow colSpan={4} />}
          </TableBody>
        </Table>
      </TableContainer>
    </MainCard>
  );
};
